import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import pdfjs, {
  GlobalWorkerOptions,
  PDFSource,
  PDFDocumentProxy,
} from 'pdfjs-dist';

import getIsCypressOnLocalhost from './getIsCypressOnLocalhost';
import useScript from './useScript';

// Adapted from Mike Cousins' usePdf example:
// https://github.com/mikecousins/react-pdf-js/blob/master/src/index.tsx

const pdfJsSrc = '//mozilla.github.io/pdf.js/legacy/build/pdf.js';
const pdfJsWorkerSrc = '//mozilla.github.io/pdf.js/legacy/build/pdf.worker.js';

type TUsePDf = {
  canvasEl?: React.RefObject<HTMLCanvasElement | null>;
  file: string;
  scale?: number;
  rotate?: number;
  page?: number;
  cMapUrl?: string;
  cMapPacked?: boolean;
  withCredentials?: boolean;
  onError?: () => void;
};

interface IPdfjs {
  GlobalWorkerOptions: GlobalWorkerOptions;
  getDocument: (config: PDFSource) => {
    promise: Promise<PDFDocumentProxy>;
  };
}

interface IPdf extends PDFDocumentProxy {
  _pdfInfo?: {
    numPages: number;
  };
}

const usePdf = ({
  canvasEl,
  file,
  scale = 1,
  rotate = 0,
  page = 1,
  cMapUrl,
  cMapPacked,
  withCredentials = false,
  onError = () => {},
}: TUsePDf) => {
  const [pdf, setPdf] = useState<IPdf>();

  const [isScriptLoaded, hasScriptError] = useScript(pdfJsSrc);

  const pdfJs = useRef<IPdfjs>();

  const memoizedOnError = useCallback(onError, []);

  useEffect(
    function assignPdfJsRef() {
      if (isScriptLoaded && !hasScriptError && !pdfJs.current && file) {
        // @ts-ignore window
        pdfJs.current = window['pdfjs-dist/build/pdf'];
        pdfJs.current!.GlobalWorkerOptions.workerSrc = pdfJsWorkerSrc;
      }
    },
    [file, hasScriptError, isScriptLoaded]
  );

  useEffect(
    function fetchPdf() {
      let isActive = true;
      if (isScriptLoaded && !hasScriptError && file) {
        const config: PDFSource = { url: file, withCredentials };
        if (cMapUrl) {
          config.cMapUrl = cMapUrl;
          config.cMapPacked = cMapPacked;
        }
        pdfJs.current
          ?.getDocument(config)
          .promise.then((pdf) => {
            if (isActive) {
              setPdf(pdf);
            }
          })
          .catch(memoizedOnError);
      }
      return () => (isActive = false);
    } as () => void,
    [
      cMapPacked,
      cMapUrl,
      memoizedOnError,
      file,
      hasScriptError,
      isScriptLoaded,
      withCredentials,
    ]
  );

  useEffect(
    function handleChange() {
      if (canvasEl) {
        const drawPDF = (page: pdfjs.PDFPageProxy) => {
          const rotation = rotate === 0 ? page.rotate : page.rotate + rotate;
          const viewport = page.getViewport({ scale, rotation });
          const canvas = canvasEl.current;
          if (!canvas) {
            return;
          }
          const canvasContext = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const renderContext = {
            canvasContext: canvasContext!,
            viewport,
          };
          page.render(renderContext);
        };
        if (pdf) {
          pdf.getPage(page).then((p) => drawPDF(p));
        }
      }
    },
    [pdf, page, scale, rotate, canvasEl]
  );

  const loading = useMemo(() => !pdf, [pdf]);
  const numPages = useMemo(() => (pdf ? pdf?._pdfInfo?.numPages : null), [pdf]);

  return [getIsCypressOnLocalhost() ? false : loading, numPages];
};

export default usePdf;
